import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { useMediaQuery, Box, Typography, Chip, Paper } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ color: '#1976d2' }} />;

// Modern stil tanımları
const styles = {
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: '#64748b',
      '&.Mui-focused': {
        color: '#1976d2',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
    },
  },
  listbox: {
    padding: '8px',
    '& .MuiAutocomplete-option': {
      borderRadius: '8px',
      margin: '2px 0',
      padding: '8px 12px',
    },
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  optionLabel: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#334155',
  },
  chip: {
    borderRadius: '8px',
    fontWeight: 500,
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    border: '1px solid #bae6fd',
    '& .MuiChip-deleteIcon': {
      color: '#0369a1',
      '&:hover': {
        color: '#0c4a6e',
      },
    },
  },
  paper: {
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    marginTop: '4px',
  },
};

const MobileColumnsAutocomplete = ({
  columns,
  selectedColumns,
  setSelectedColumns
}) => {

  const [selectedColumnObjects, setSelectedColumnObjects] = useState([]);

  const handleChange = (event, newValue) => {
    setSelectedColumns(newValue.map(option => option.headerName));
    setSelectedColumnObjects(newValue);
  };
  
  useEffect(() => {
    const selectedObjects = columns.filter(col => selectedColumns.includes(col.headerName));
    setSelectedColumnObjects(selectedObjects);
  }, [selectedColumns, columns]);

  return (
    <Autocomplete
      multiple
      options={columns}
      value={selectedColumnObjects}
      onChange={handleChange}
      disableCloseOnSelect
      getOptionLabel={option => option.headerName}
      sx={styles.autocomplete}
      ListboxProps={{
        sx: styles.listbox,
      }}
      PaperComponent={({ children }) => (
        <Paper sx={styles.paper}>{children}</Paper>
      )}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Box sx={styles.optionContent}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              sx={{ padding: '4px' }}
            />
            <Typography sx={styles.optionLabel}>
              {option.headerName}
            </Typography>
          </Box>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.field || index}
            label={option.headerName}
            size="small"
            sx={styles.chip}
          />
        ))
      }
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label="Görüntülenecek Kolonlar"
          placeholder={selectedColumnObjects.length === 0 ? "Kolon seçiniz..." : ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <ViewColumnIcon sx={{ color: '#94a3b8', marginLeft: '8px', marginRight: '-4px' }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        <Box sx={{ padding: '16px', textAlign: 'center' }}>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Kolon bulunamadı
          </Typography>
        </Box>
      }
    />
  );
};

export default MobileColumnsAutocomplete;
